import {
  InfiniteData,
  QueryKey,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { allCompanyMembersKey, companyMembersKey } from './keys'
import {
  AllCompaniesMembersResponse,
  EditCompanyGroupMemberCompaniesRequest,
  getAllCompaniesMembers,
  getCompanyMembers,
  updateCompanyGroupMemberCompanies,
} from 'api/company'
import { useAuthContext } from 'components/auth'

export function useCompanyMembers() {
  return useQuery({
    queryKey: companyMembersKey,
    queryFn: getCompanyMembers,
  })
}

export function useInvalidateCompanyMembers() {
  const queryClient = useQueryClient()

  return {
    invalidate: () =>
      queryClient.invalidateQueries({ queryKey: companyMembersKey }),
  }
}

export function useInvalidateCompanyGroupMembers(companyId: number) {
  const queryClient = useQueryClient()

  return {
    invalidate: () =>
      queryClient.invalidateQueries({
        queryKey: allCompanyMembersKey(companyId),
      }),
  }
}

export function useAllCompanyMembers(searchTerm?: string, limit?: number) {
  const { company } = useAuthContext()

  return useInfiniteQuery<
    AllCompaniesMembersResponse,
    Error,
    InfiniteData<AllCompaniesMembersResponse, string | undefined>,
    QueryKey,
    string | undefined
  >({
    queryKey: allCompanyMembersKey(company?.id ?? 0, searchTerm, limit),
    queryFn: ({ pageParam }) =>
      getAllCompaniesMembers(company?.id ?? 0, searchTerm, pageParam, limit),
    enabled: !!company?.id,
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
  })
}

export function useUpdateCompanyGroupMemberCompanies() {
  const { company } = useAuthContext()
  const { invalidate: invalidateCompanyGroupMembers } =
    useInvalidateCompanyGroupMembers(company?.id ?? 0)

  return useMutation({
    mutationFn: (request: EditCompanyGroupMemberCompaniesRequest) =>
      updateCompanyGroupMemberCompanies(company?.id ?? 0, request),
    onSuccess: () => {
      invalidateCompanyGroupMembers()
    },
  })
}
